import React, { useCallback, useEffect, useState } from 'react';
import PageHeaderText from 'components/base/page-header-text';
import PaymentTrackerFilter from './payment-tracker-filter';
import { DATE_RANGE_TYPE, DROP_DOWN_OPTIONS_TYPE, ENUM_PAYMENT_STATUS } from 'models/common.model';
import { ALL, STORAGE_KEYS } from 'utils/constants';
import { debounce } from 'utils/functions';
import { ENUM_DATE_RANGE, ToastMessage } from '@cokitchen/cokitchen-components';
import { ReactComponent as NoProcurement } from 'assets/icons/no-procurement.svg';
import { toast } from 'react-toastify';
import { apiService } from 'services/base.service';
import PaymentCard from 'components/payment-card';
import ActivityIndicator from 'components/base/loader/activityIndicator';
import { PaymentRequestDetailType } from 'models/payment-tracker.model';

export type ExtendedPaymentType = ENUM_PAYMENT_STATUS | typeof ALL;

export type FilterType = {
  search: string;
  status: ExtendedPaymentType;
  dateRange: DATE_RANGE_TYPE;
  vendor: DROP_DOWN_OPTIONS_TYPE;
  current_page: number;
};

type PaymentRequestParams = {
  page: number;
  size: number;
  store_id?: string;
  code?: string;
  status?: string;
  vendor_id?: string;
  start_date?: string;
  end_date?: string;
};

const PaymentTracker = () => {
  const [filteredPaymentTracker, setFilteredPaymentTracker] =
    useState<PaymentRequestDetailType[]>();
  const [isFetchingPayments, setIsFetchingPayments] = useState(false);
  const [lastPage, setLastPage] = useState(1);

  const [filters, setFilters] = useState({
    search: '',
    status: ALL,
    vendor: { text: 'All vendors', value: ALL },
    dateRange: {
      startDate: new Date(),
      endDate: new Date(),
      range: ENUM_DATE_RANGE.ALL
    },
    current_page: 1
  } as FilterType);

  const clearVendorsList = () => setFilteredPaymentTracker([]);

  useEffect(() => {
    setIsFetchingPayments(true);
    clearVendorsList();
    debounceFetchVendors(filters);
  }, [filters]);

  const fetchRequests = async (filters: FilterType) => {
    try {
      setIsFetchingPayments(true);
      const storedStore = sessionStorage.getItem(STORAGE_KEYS.ACTIVE_STORE);
      const storeId = storedStore ? JSON.parse(storedStore)?.id : '';

      const params: PaymentRequestParams = {
        page: filters.current_page,
        size: 15,
        ...(storeId ? { store_id: storeId } : {}),
        ...(filters.search ? { code: filters.search } : {}),
        ...(filters.status !== ALL ? { status: filters.status } : {}),
        ...(filters.vendor.value !== ALL ? { vendor_id: filters.vendor.value } : {})
      };

      if (filters.dateRange.range !== ENUM_DATE_RANGE.ALL) {
        params.start_date = filters.dateRange.startDate.toISOString();
        params.end_date = filters.dateRange.endDate.toISOString();
      }

      const response = await apiService.get('payment-request', {
        version: 'v2',
        params
      });
      if (response) {
        setFilteredPaymentTracker(response.data.data);
        setLastPage(Math.ceil(response.data.pagination.total / response.data.pagination.page_size));
      }
    } catch (err: any) {
      toast(
        <ToastMessage
          message={err.response?.data?.message || 'Error fetching payments'}
          type='error'
        />
      );
    } finally {
      setIsFetchingPayments(false);
    }
  };

  const debounceFetchVendors = useCallback(debounce(fetchRequests, 1000), []);

  return (
    <div>
      <PageHeaderText title='Manage Payments' description='Manage payment requests' />
      <PaymentTrackerFilter filters={filters} setFilters={setFilters} pageCount={lastPage} />

      {isFetchingPayments ? (
        <div className='pt-10'>
          <ActivityIndicator />
        </div>
      ) : filteredPaymentTracker?.length === 0 ? (
        <div className='grid place-items-center mt-24'>
          <NoProcurement className='mb-5' />
          <p className='font-medium text-[#5E6164]'>There are currenly no payments</p>
        </div>
      ) : (
        <div className='mt-10 gap-7 grid grid-cols-3 max-[1250px]:grid-cols-2 max-[768px]:grid-cols-1'>
          {filteredPaymentTracker?.map((p, i) => (
            <div key={i}>
              <PaymentCard key={i} request={p} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PaymentTracker;
